import {
    type ComponentProps,
    type FC,
    type ReactElement,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';
import { graphql, usePaginationFragment } from 'react-relay';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { FormattedMessage } from 'dibs-react-intl';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';
import { getProductTileAdData } from 'dibs-google-ad-manager/exports/adHelpers.ts';
import { HOME_MIXED_GRID_PLACEMENT } from 'dibs-google-ad-manager/exports/constants.ts';
import { CONSUMER, TRADE } from 'dibs-buyer-layout/exports/userTypeEnums.ts';

import { useHomePageContext } from '../homepage/HomePageContext.tsx';
import {
    getHpPersonalizedMixedGridFetchCount,
    getHpPersonalizedMixedGridShimmerCount,
    getHpPersonalizedMixedGridVisibleEdges,
    MIXED_GRID_MOBILE_SLOT_COUNT,
} from './buildHpPersonalizedMixedGrid.ts';
import { HpPersonalizedMixedGridMobile } from './HpPersonalizedMixedGridMobile.tsx';
import { HpPersonalizedMixedGridResp } from './HpPersonalizedMixedGridResp.tsx';

import { type HpPersonalizedMixedGridPaginationQuery } from './__generated__/HpPersonalizedMixedGridPaginationQuery.graphql.ts';
import { type HpPersonalizedMixedGrid_viewer$key } from './__generated__/HpPersonalizedMixedGrid_viewer.graphql.ts';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment HpPersonalizedMixedGrid_viewer on Viewer
    @refetchable(queryName: "HpPersonalizedMixedGridPaginationQuery")
    @argumentDefinitions(
        recommendationType: { type: "UserRecommendationType!" }
        isPersonalizedContentAboveFoldTestVariant: { type: "Boolean!" }
        mixedGridFirst: { type: "Int", defaultValue: 8 }
        mixedGridAfter: { type: "String" }
    ) {
        ...HpPersonalizedMixedGridMobile_viewer
            @arguments(
                recommendationType: $recommendationType
                isPersonalizedContentAboveFoldTestVariant: $isPersonalizedContentAboveFoldTestVariant
                mixedGridFirst: $mixedGridFirst
                mixedGridAfter: $mixedGridAfter
            )
            @include(if: $isMobile)
        ...HpPersonalizedMixedGridResp_viewer
            @arguments(
                recommendationType: $recommendationType
                isPersonalizedContentAboveFoldTestVariant: $isPersonalizedContentAboveFoldTestVariant
                mixedGridFirst: $mixedGridFirst
                mixedGridAfter: $mixedGridAfter
            )
            @skip(if: $isMobile)
        homepageMixedGrid(
            homepageId: $homepageId
            userId: $userId
            userType: $userType
            recommendationType: $recommendationType
            abTestList: $abTestList
            isMobile: $isMobile
        ) @include(if: $isPersonalizedContentAboveFoldTestVariant) {
            items(first: $mixedGridFirst, after: $mixedGridAfter)
                @connection(key: "HpPersonalizedMixedGrid_items") {
                edges {
                    node {
                        item {
                            __typename
                        }
                    }
                }
            }
        }
        user(userId: $userId) @include(if: $hasUserId) {
            profile {
                firstName
            }
        }
    }
`;

type Props = {
    viewer: HpPersonalizedMixedGrid_viewer$key;
    onItemTileCountChange: (count: number) => void;
    afterDisplay: boolean;
};

export const HpPersonalizedMixedGrid: FC<Props> = ({
    viewer: viewerRef,
    onItemTileCountChange,
    afterDisplay,
}) => {
    const {
        data: viewer,
        loadNext,
        isLoadingNext,
    } = usePaginationFragment<
        HpPersonalizedMixedGridPaginationQuery,
        HpPersonalizedMixedGrid_viewer$key
    >(viewerFragment, viewerRef);
    const {
        isMobile,
        isTopDisplayAdEnabled,
        isTrade,
        serverCurrency = 'USD',
    } = useHomePageContext();
    const [hasAdFill, setHasAdFill] = useState(!!isTopDisplayAdEnabled);
    const [hasFinishedLoadMore, setHasFinishedLoadMore] = useState(false);
    const wasLoadingNextRef = useRef(false);

    const mixedGrid = viewer.homepageMixedGrid;
    const edges = (mixedGrid?.items?.edges || []).filter(filterFalsy);
    const firstName = viewer.user?.profile?.firstName?.trim() || '';
    const showFirstName = !!firstName && (!isMobile || firstName.length <= 13);
    const adData = useMemo(
        () =>
            getProductTileAdData({
                uriRef: HOME_MIXED_GRID_PLACEMENT,
                userType: isTrade ? TRADE : CONSUMER,
                deviceType: isMobile ? 'mobile' : 'desktop',
            })[0],
        [isMobile, isTrade]
    );
    const includeAd = !!isTopDisplayAdEnabled && !!adData && hasAdFill;
    const visibleEdges = getHpPersonalizedMixedGridVisibleEdges(edges, includeAd);
    const listingItemCount = visibleEdges.filter(
        edge => edge.node?.item?.__typename === 'Item'
    ).length;
    const itemTileCount = isMobile
        ? Math.min(listingItemCount, MIXED_GRID_MOBILE_SLOT_COUNT)
        : listingItemCount;
    const remainingItemCount = getHpPersonalizedMixedGridFetchCount(isMobile) - edges.length;
    const shimmerCount =
        remainingItemCount && !hasFinishedLoadMore
            ? getHpPersonalizedMixedGridShimmerCount(
                  isMobile ? itemTileCount : visibleEdges.length,
                  isMobile
              )
            : 0;

    useEffect(() => {
        if (wasLoadingNextRef.current && !isLoadingNext) {
            setHasFinishedLoadMore(true);
        }
        wasLoadingNextRef.current = isLoadingNext;
    }, [isLoadingNext]);

    useEffect(() => {
        if (remainingItemCount > 0 && !hasFinishedLoadMore) {
            return;
        }
        onItemTileCountChange(itemTileCount);
    }, [hasFinishedLoadMore, itemTileCount, onItemTileCountChange, remainingItemCount]);

    const handleAbsentAd = useCallback(
        (elementId: string) => {
            if (elementId === adData?.elementId) {
                setHasAdFill(false);
            }
        },
        [adData?.elementId]
    );

    const handleLoadMoreVisibilityChange: ComponentProps<
        typeof VisibilityTracker
    >['onVisibilityChange'] = useCallback(
        ({ isVisible }) => {
            if (isVisible && remainingItemCount > 0 && !isLoadingNext) {
                loadNext(remainingItemCount);
            }
        },
        [isLoadingNext, loadNext, remainingItemCount]
    );

    if (!itemTileCount) {
        return null;
    }

    const italicClassName = isMobile
        ? dibsCss.sassyFontHeaderLargeItalic
        : dibsCss.sassyFontHeaderXxLargeItalic;
    const italicWrapper = (chunks: ReactNode[]): ReactElement => (
        <span className={italicClassName}>{chunks}</span>
    );
    const loadMoreTracker = afterDisplay ? (
        <VisibilityTracker
            key="hp-personalized-mixed-grid-load-more-tracker"
            observerOptions={{
                rootMargin: isMobile ? '700px 0px' : '400px 0px',
            }}
            onVisibilityChange={handleLoadMoreVisibilityChange}
        />
    ) : null;

    return (
        <>
            <section
                className={classnames({
                    [dibsCss.borderB]: !isMobile,
                    [dibsCss.borderBSolid]: !isMobile,
                    [dibsCss.borderBDolphin]: !isMobile,
                    [dibsCss.pbLarge]: !isMobile,
                })}
                data-tn="hp-personalized-mixed-grid"
            >
                <div
                    className={classnames(dibsCss.myLarge, dibsCss.textCenter, {
                        [dibsCss.sassyFontHeaderLarge]: isMobile,
                        [dibsCss.sassyFontHeaderXxLarge]: !isMobile,
                    })}
                >
                    {showFirstName ? (
                        <FormattedMessage
                            id="abm.HpPersonalizedMixedGrid.titleWithName"
                            defaultMessage="{firstName}, Your <italicWrapper>Daily Edit</italicWrapper>"
                            values={{
                                firstName,
                                italicWrapper,
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="abm.HpPersonalizedMixedGrid.title"
                            defaultMessage="Your <italicWrapper>Daily Edit</italicWrapper>"
                            values={{
                                italicWrapper,
                            }}
                        />
                    )}
                </div>
                {isMobile ? (
                    <HpPersonalizedMixedGridMobile
                        viewer={viewer}
                        afterDisplay={afterDisplay}
                        currency={serverCurrency}
                        includeAd={includeAd}
                        shimmerCount={shimmerCount}
                        loadMoreTracker={loadMoreTracker}
                    />
                ) : (
                    <HpPersonalizedMixedGridResp
                        viewer={viewer}
                        afterDisplay={afterDisplay}
                        currency={serverCurrency}
                        includeAd={includeAd}
                        onAbsentAd={handleAbsentAd}
                        shimmerCount={shimmerCount}
                        loadMoreTracker={loadMoreTracker}
                    />
                )}
            </section>
            {isMobile && (
                <div
                    className={classnames(
                        dibsCss.borderB,
                        dibsCss.borderBSolid,
                        dibsCss.borderBDolphin
                    )}
                />
            )}
        </>
    );
};
